// Hook to check user onboarding status
// Determines if a user needs to complete onboarding flow

import { useState, useEffect } from 'react';
import { useUser } from '../contextapi/UserContext';
import { supabase } from '../utils/supabaseClient';

export interface OnboardingStatus {
  isOnboarded: boolean;
  isLoading: boolean;
  error: string | null;
  needsOnboarding: boolean;
  hasWorkspace: boolean;
  currentStep?: number;
}

export const useOnboardingStatus = (): OnboardingStatus & { refresh: () => void } => {
  const { user } = useUser();
  const [isOnboarded, setIsOnboarded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [hasWorkspace, setHasWorkspace] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Check user profile for onboarding status
        // Use RPC function to bypass RLS issues
        const { data: profileData, error: profileError } = await supabase.rpc(
          'get_user_profile',
          { user_id: user.id }
        );

        if (profileError) {
          // If profile doesn't exist, user needs onboarding
          if (profileError.code === 'PGRST116') {
            setIsOnboarded(false);
            setHasWorkspace(false);
            setCurrentStep(1);
          } else if (profileError.code === 'PGRST301' || profileError.message?.includes('403')) {
            // Handle 403 Forbidden - user exists but can't access profile
            // This might happen if the profile was created by trigger but RLS is blocking access
            console.warn('Profile access denied, assuming user needs onboarding');
            setIsOnboarded(false);
            setHasWorkspace(false);
            setCurrentStep(1);
          } else {
            throw profileError;
          }
        } else {
          // Check if profile exists and has data
          // profileData is an array from the RPC function
          if (profileData && profileData.length > 0) {
            const profile = profileData[0]; // Get first (and should be only) profile
            console.log('🔍 Profile data:', profile); // Debug log
            
            setIsOnboarded(profile.is_onboarded || false);
            setHasWorkspace(!!profile.tenant_id);
          
            // If not onboarded, check if there's an active onboarding session
            if (!profile.is_onboarded) {
              const { data: sessionData } = await supabase
                .from('base_onboarding_sessions')
                .select('current_step, status, completed_at')
                .eq('user_id', user.id)
                .eq('status', 'in_progress') // Only get active sessions
                .order('created_at', { ascending: false })
                .limit(1)
                .single();
              
              const session = sessionData;

              if (session) {
                setCurrentStep(session.current_step || 1);
              }
            } else {
              // User is onboarded, check if there's a completed session to get the final step
              const { data: sessionData } = await supabase
                .from('base_onboarding_sessions')
                .select('current_step, status, completed_at')
                .eq('user_id', user.id)
                .eq('status', 'completed')
                .order('completed_at', { ascending: false })
                .limit(1)
                .single();
              
              const session = sessionData;
              if (session) {
                setCurrentStep(session.current_step || 3);
              }
            }
          } else {
            // Profile doesn't exist, user needs onboarding
            console.log('🔍 No profile data found, user needs onboarding');
            setIsOnboarded(false);
            setHasWorkspace(false);
            setCurrentStep(1);
          }
        }
      } catch (err) {
        console.error('Error checking onboarding status:', err);
        
        // Handle 403 Forbidden errors more gracefully
        if (err instanceof Error && (err.message.includes('403') || err.message.includes('Forbidden'))) {
          console.warn('403 Forbidden error detected in onboarding check, assuming user needs onboarding');
          // Don't sign out automatically - just assume user needs onboarding
          setIsOnboarded(false);
          setHasWorkspace(false);
          setCurrentStep(1);
        } else {
          setError(err instanceof Error ? err.message : 'Failed to check onboarding status');
          // Default to needing onboarding on error
          setIsOnboarded(false);
          setHasWorkspace(false);
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [user?.id, refreshTrigger]);

  const refresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return {
    isOnboarded,
    isLoading,
    error,
    needsOnboarding: !isOnboarded,
    hasWorkspace,
    currentStep,
    refresh
  };
};
